/**
 * WebAuthn / passkey browser helpers (registration + management).
 *
 * account-service's passkey endpoints speak the standard WebAuthn JSON shapes
 * produced by the Go `go-webauthn` library: binary fields travel as base64url
 * strings, while the browser `navigator.credentials` API works with
 * `ArrayBuffer`s. These helpers translate between the two for the enrollment
 * (register) ceremony used by the Passkeys settings screen.
 */

/**
 * Decodes a base64url string into an `ArrayBuffer`.
 *
 * @param {string} value base64url-encoded data (padded or unpadded).
 * @returns {ArrayBuffer} the decoded bytes.
 */
function base64urlToBuffer(value: string): ArrayBuffer {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64.padEnd(
    base64.length + ((4 - (base64.length % 4)) % 4),
    "="
  );
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Encodes an `ArrayBuffer` as an unpadded base64url string.
 *
 * @param {ArrayBuffer} buffer the bytes to encode.
 * @returns {string} the base64url representation.
 */
function bufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

/** A credential descriptor as serialized by go-webauthn (binary id base64url). */
type CredentialDescriptorJSON = {
  type: PublicKeyCredentialType;
  id: string;
  transports?: AuthenticatorTransport[];
};

/**
 * The `options` returned by `/users/passkey/register/options`. go-webauthn's
 * `protocol.CredentialCreation` marshals the creation options under `publicKey`.
 */
export type PasskeyRegisterOptions = {
  publicKey: {
    rp: { id?: string; name: string };
    user: { id: string; name: string; displayName: string };
    challenge: string;
    pubKeyCredParams: { type: PublicKeyCredentialType; alg: number }[];
    timeout?: number;
    excludeCredentials?: CredentialDescriptorJSON[];
    authenticatorSelection?: AuthenticatorSelectionCriteria;
    attestation?: AttestationConveyancePreference;
    extensions?: AuthenticationExtensionsClientInputs;
  };
};

/**
 * The attestation payload posted to `/users/passkey/register/verify`. Matches the
 * shape go-webauthn's `ParseCredentialCreationResponseBytes` expects.
 */
export type SerializedAttestation = {
  id: string;
  rawId: string;
  type: string;
  authenticatorAttachment?: string;
  clientExtensionResults: AuthenticationExtensionsClientOutputs;
  response: {
    clientDataJSON: string;
    attestationObject: string;
    transports?: string[];
  };
};

/**
 * Reports whether the current browser exposes the WebAuthn API.
 *
 * @returns {boolean} true when `navigator.credentials` / `PublicKeyCredential` exist.
 */
export function isPasskeySupported(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.PublicKeyCredential !== "undefined" &&
    typeof navigator !== "undefined" &&
    !!navigator.credentials
  );
}

/**
 * Converts the server's JSON creation options into the `ArrayBuffer`-based
 * options `navigator.credentials.create()` requires.
 *
 * @param {PasskeyRegisterOptions} options the options returned by the server.
 * @returns {PublicKeyCredentialCreationOptions} browser-ready creation options.
 */
function toCreationOptions(
  options: PasskeyRegisterOptions
): PublicKeyCredentialCreationOptions {
  const pk = options.publicKey;
  return {
    rp: pk.rp,
    user: {
      id: base64urlToBuffer(pk.user.id),
      name: pk.user.name,
      displayName: pk.user.displayName,
    },
    challenge: base64urlToBuffer(pk.challenge),
    pubKeyCredParams: pk.pubKeyCredParams,
    timeout: pk.timeout,
    attestation: pk.attestation,
    authenticatorSelection: pk.authenticatorSelection,
    extensions: pk.extensions,
    excludeCredentials: pk.excludeCredentials?.map((credential) => ({
      type: credential.type,
      id: base64urlToBuffer(credential.id),
      transports: credential.transports,
    })),
  };
}

/**
 * Serializes the attestation into the base64url JSON the verify endpoint expects.
 *
 * @param {PublicKeyCredential} credential the credential from `navigator.credentials.create()`.
 * @returns {SerializedAttestation} the JSON-safe attestation payload.
 */
function serializeAttestation(
  credential: PublicKeyCredential
): SerializedAttestation {
  const response = credential.response as AuthenticatorAttestationResponse;
  const transports =
    typeof response.getTransports === "function"
      ? response.getTransports()
      : undefined;
  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    authenticatorAttachment: credential.authenticatorAttachment ?? undefined,
    clientExtensionResults: credential.getClientExtensionResults(),
    response: {
      clientDataJSON: bufferToBase64url(response.clientDataJSON),
      attestationObject: bufferToBase64url(response.attestationObject),
      transports: transports && transports.length ? transports : undefined,
    },
  };
}

/**
 * Runs the browser side of the passkey enrollment ceremony: prompts the
 * authenticator via `navigator.credentials.create()` and serializes the result.
 *
 * @param {PasskeyRegisterOptions} options the options from `register/options`.
 * @returns {Promise<SerializedAttestation>} the attestation to post to `register/verify`.
 * @throws {DOMException} `NotAllowedError` when the user cancels, etc.
 */
export async function createPasskeyCredential(
  options: PasskeyRegisterOptions
): Promise<SerializedAttestation> {
  const credential = (await navigator.credentials.create({
    publicKey: toCreationOptions(options),
  })) as PublicKeyCredential | null;
  if (!credential) {
    throw new Error("No passkey credential was created.");
  }
  return serializeAttestation(credential);
}

/**
 * Best-effort human-readable label for a new passkey, from the browser/OS.
 *
 * @returns {string} a label for the credential, or "Passkey" when undetectable.
 */
export function defaultPasskeyLabel(): string {
  if (typeof navigator === "undefined") return "Passkey";
  const ua = navigator.userAgent;
  const platform = /iPhone|iPad|iPod/.test(ua)
    ? "iOS"
    : /Macintosh/.test(ua)
    ? "macOS"
    : /Android/.test(ua)
    ? "Android"
    : /Windows/.test(ua)
    ? "Windows"
    : /Linux/.test(ua)
    ? "Linux"
    : "";
  const browser = /Edg\//.test(ua)
    ? "Edge"
    : /Chrome\//.test(ua)
    ? "Chrome"
    : /Firefox\//.test(ua)
    ? "Firefox"
    : /Safari\//.test(ua)
    ? "Safari"
    : "";
  const parts = [browser, platform].filter(Boolean);
  return parts.length ? parts.join(" on ") : "Passkey";
}

/**
 * Maps a ceremony or transport error into a user-facing message.
 *
 * @param {unknown} error the rejection from the ceremony or a request.
 * @returns {string} a message safe to surface in the UI.
 */
export function passkeyErrorMessage(error: unknown): string {
  if (error instanceof DOMException) {
    if (error.name === "NotAllowedError" || error.name === "AbortError") {
      return "Passkey setup was cancelled.";
    }
    if (error.name === "InvalidStateError") {
      return "A passkey is already set up on this device.";
    }
    if (error.name === "SecurityError") {
      return "Passkey setup is not allowed on this domain.";
    }
  }
  if (typeof error === "object" && error !== null && "data" in error) {
    const data = (error as { data?: unknown }).data;
    if (typeof data === "string") return data;
    if (
      typeof data === "object" &&
      data !== null &&
      "description" in data &&
      typeof (data as { description?: unknown }).description === "string"
    ) {
      return (data as { description: string }).description;
    }
  }
  if (error instanceof Error && error.message) return error.message;
  return "Passkey setup failed.";
}
